import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

type HashAccordionOptions = {
    /**
     * What a record is called when a link names it rather than numbering it.
     * Links written before the lists were rebuilt point at names —
     * /browse_services/#database — and go on arriving from bookmarks, tickets
     * and the search bar, so a list that has names opens from either.
     */
    aliasOf?: (record: any) => string | undefined;
};

/** one token, comparable: ids and names are matched the same way */
const key = (value: unknown) => String(value ?? '').trim().toLowerCase();

/** a hash carries whatever a name carries, so it travels encoded */
const decode = (hash: string) => {
    const raw = hash.replace(/^#/, '');
    try {
        return decodeURIComponent(raw);
    } catch {
        // a half-written escape is not a row, but it is not a crash either
        return raw;
    }
};

/**
 * One row open at a time, named in the URL hash.
 *
 * The open row is addressable: it survives a reload and can be linked to from
 * elsewhere — a notification about a rejected change, say. Only a hash *being
 * set* opens a row, because react-admin rewrites the search string whenever
 * list params change, which drops the hash, and that must not collapse what the
 * reader is in the middle of. Closing is driven by toggle().
 *
 * The hash names a row by id, or by whatever `aliasOf` calls it. Both open the
 * same row whichever the list writes, so a link that has been sitting in
 * someone's notes for two years still lands where it always did.
 *
 * `scrollsTo` is the row an incoming link asked for. Hand it to that row and
 * let it scroll itself once it has rendered: a list cannot know when its rows
 * reach the DOM, and a row that is not in the result set simply never scrolls.
 *
 * `wanted` / `matches` / `settle` are for the list around this. Pages arrive on
 * scroll, so the row a link asked for may not be loaded at all, and only the
 * list can go and fetch it — see useInfinitePages' seek, which reports back
 * through settle() once the row turns up or the search is given up.
 */
const useHashAccordion = ({ aliasOf }: HashAccordionOptions = {}) => {
    const location = useLocation();
    const history = useHistory();

    const [openKey, setOpenKey] = useState<string | null>(null);
    const [scrollKey, setScrollKey] = useState<string | null>(null);
    const [seekKey, setSeekKey] = useState<string | null>(null);

    // toggle() writes the hash too, and that must not read back as a deep link
    const selfNavigated = useRef(false);

    useEffect(() => {
        const token = key(decode(location.hash));
        if (token) {
            setOpenKey(token);
            // a click already put the row under the cursor — only an incoming
            // link needs the list scrolled, or gone looking through
            if (!selfNavigated.current) {
                setScrollKey(token);
                setSeekKey(token);
            }
        }
        selfNavigated.current = false;
    }, [location.hash]);

    /** what this record is called in a link */
    const linkName = (record: any) => (aliasOf && aliasOf(record)) || String(record?.id ?? '');

    const named = (record: any, token: string | null) => !!token && (
        key(record?.id) === token || key(aliasOf && aliasOf(record)) === token
    );

    const toggle = (record: any) => {
        const name = linkName(record);
        const next = named(record, openKey) ? null : key(name);
        setOpenKey(next);
        // whatever the link asked for, the reader has moved on from it
        setScrollKey(null);
        setSeekKey(null);
        selfNavigated.current = true;
        // keep the open row addressable
        const hash = next ? `#${encodeURIComponent(name)}` : '';
        history.push(`${location.pathname}${location.search}${hash}`);
    };

    return {
        isOpen: (record: any) => named(record, openKey),
        scrollsTo: (record: any) => named(record, scrollKey),
        toggle,
        /** the row a link asked for, until the list has produced it or given up */
        wanted: seekKey,
        matches: (record: any) => named(record, seekKey),
        /** stop looking: it is on screen, or it is not coming */
        settle: () => setSeekKey(null),
    };
};

export default useHashAccordion;
